import type { ComponentPropsWithRef } from "react";
import { forwardRef } from "react";
import { Hr, Section } from "@react-email/components";
import { noop } from "lodash";
import { useGetMarketingEmailAddressSettings } from "@/react/components/SettingsApp/Emails/hooks/useGetMarketingEmailAddressSettings";
import { usePunditUserContext } from "@/react/contexts/punditUserContext";
import { AddressSection } from "./AddressSection";
import { CommunityUrlSection } from "./CommunityUrlSection";
import { LogoSection } from "./LogoSection";
import { PreferenceSection } from "./PreferenceSection";
import { defaultStyles } from "./defaultStyles";

interface FooterBlockProps extends ComponentPropsWithRef<"table"> {
  "data-preview-mode": boolean;
}

export const FooterBlockComponent = forwardRef<
  HTMLTableElement,
  FooterBlockProps
>(({ className, "data-preview-mode": isPreviewMode, ...rest }, ref) => {
  const { data: footerData, isLoading: isLoadingFooter } =
    useGetMarketingEmailAddressSettings();
  const { currentCommunity, isLoading: isLoadingCommunity } =
    usePunditUserContext();

  const handleClick = () => {
    // We do not allow clicking on the footer block and editing it.
    // Hence, we've overridden the onClick handler to do nothing.
    noop();
  };

  if (isLoadingFooter || isLoadingCommunity || !currentCommunity) {
    return null;
  }

  return (
    <Section
      style={defaultStyles.section}
      ref={ref}
      className={className}
      data-no-drop-below="true"
      {...rest}
      onClick={handleClick}
    >
      <Hr style={defaultStyles.hr} />
      <LogoSection
        logoUrl={currentCommunity?.logo_url}
        iconUrl={currentCommunity?.icon_url}
        communityName={currentCommunity?.name}
      />
      <CommunityUrlSection
        rootUrl={currentCommunity?.root_url}
        isPreviewMode={isPreviewMode}
      />
      <AddressSection address={footerData?.formatted_address || null} />
      <PreferenceSection isPreviewMode={isPreviewMode} />
    </Section>
  );
});

FooterBlockComponent.displayName = "FooterBlockComponent";
